import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  GraduationCap, ArrowRight, ArrowLeft, X, Play, Pause, 
  TreeStructure, SquaresFour, FlowArrow, Cpu, CheckCircle,
  Lightbulb, Target, Info
} from "@phosphor-icons/react";

const TOUR_STEPS = {
  overview: [
    {
      id: "welcome",
      title: "Welcome to CME Operating Map",
      description: "This interactive map helps you understand how CME Envirosystems operates. You can explore roles, departments, processes, and systems.",
      highlight: null,
      action: null,
      icon: GraduationCap,
      tips: ["Use the tabs to switch between different views", "Click on any node to see details", "Press ⌘K to search"],
    },
    {
      id: "org-view",
      title: "Organization View",
      description: "See who reports to whom, understand the team structure, and explore individual role responsibilities.",
      highlight: "org",
      action: "switchView",
      actionParam: "org",
      icon: TreeStructure,
      tips: ["Each card represents a role", "Lines show reporting relationships", "Click a role to see KPIs, responsibilities, and handoffs"],
    },
    {
      id: "function-view",
      title: "Function View",
      description: "Explore departments like Sales, R&D, Production, and Quality Control. See how work flows between them.",
      highlight: "function",
      action: "switchView",
      actionParam: "function",
      icon: SquaresFour,
      tips: ["Colors indicate different departments", "Arrows show handoff directions", "Bottlenecks are highlighted in red"],
    },
    {
      id: "process-view",
      title: "Process View",
      description: "Understand step-by-step workflows like Order to Dispatch or Installation & Commissioning.",
      highlight: "process",
      action: "switchView",
      actionParam: "process",
      icon: FlowArrow,
      tips: ["Each process shows numbered steps", "Dependencies link related processes", "Click to see full workflow details"],
    },
    {
      id: "system-view",
      title: "System View",
      description: "See all the software and tools used: Zoho CRM, Asana, DriveWorks, Eazy ERP, and more.",
      highlight: "system",
      action: "switchView",
      actionParam: "system",
      icon: Cpu,
      tips: ["Click to open external systems", "See which roles use each system", "API connections show live data"],
    },
    {
      id: "complete",
      title: "Tour Complete!",
      description: "You're now ready to explore the CME Operating Map. Use Training Mode for guided navigation or Presentation Mode for stakeholder meetings.",
      highlight: null,
      action: null,
      icon: CheckCircle,
      tips: ["Training Mode animates the flow", "Presentation Mode enlarges nodes", "Admin panel lets you edit data"],
    },
  ],
  orderToDispatch: [
    {
      id: "intro",
      title: "Order to Dispatch Process",
      description: "This tour walks you through the complete journey from receiving a purchase order to dispatching the product.",
      highlight: null,
      icon: FlowArrow,
      focusNode: null,
    },
    {
      id: "step1",
      title: "Step 1: PO Receipt",
      description: "Sales team receives the Purchase Order. An automatic email is sent to the customer confirming the Commercial Acceptance.",
      highlight: "process",
      icon: Target,
      focusNode: "proc_order_dispatch",
    },
    {
      id: "step2",
      title: "Step 2: Work Order Release",
      description: "Projects & R&D releases the Work Order with SOS (Statement of Scope) to PPC for planning.",
      highlight: "function",
      icon: Target,
      focusNode: "dept_rnd",
    },
    {
      id: "step3",
      title: "Step 3: Design & BOM",
      description: "PED Project Engineer generates mechanical drawings using DriveWorks and creates the BOM (Bill of Materials).",
      highlight: "org",
      icon: Target,
      focusNode: "role_ped",
    },
    {
      id: "step4",
      title: "Step 4: Procurement",
      description: "SCM Manager uploads BOM to Eazy ERP, runs MRP daily, and manages vendor procurement.",
      highlight: "org",
      icon: Target,
      focusNode: "role_scm_manager",
    },
    {
      id: "step5",
      title: "Step 5: Production",
      description: "Production Team executes manufacturing as per PPC plan, with inprocess inspection at every stage.",
      highlight: "function",
      icon: Target,
      focusNode: "dept_production",
    },
    {
      id: "step6",
      title: "Step 6: Quality Control",
      description: "QC Head performs final inspection and generates the Chamber Readiness Report.",
      highlight: "org",
      icon: Target,
      focusNode: "role_qc_head",
    },
    {
      id: "step7",
      title: "Step 7: Dispatch",
      description: "Business Office coordinates customer acceptance, documentation, and finally dispatch clearance.",
      highlight: "function",
      icon: Target,
      focusNode: "dept_bo",
    },
    {
      id: "complete",
      title: "Process Complete",
      description: "You've walked through the Order to Dispatch process. The product is now ready for installation!",
      highlight: null,
      icon: CheckCircle,
      focusNode: null,
    },
  ],
};

export default function TrainingTour({ 
  open, 
  onOpenChange, 
  tourType = "overview",
  onSwitchView,
  onFocusNode,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const steps = TOUR_STEPS[tourType] || TOUR_STEPS.overview;
  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setAutoPlay(false);
    }
  }, [open]);

  useEffect(() => {
    if (step?.action === "switchView" && onSwitchView) {
      onSwitchView(step.actionParam);
    }
    if (step?.focusNode && onFocusNode) {
      onFocusNode(step.focusNode);
    }
  }, [currentStep, step, onSwitchView, onFocusNode]);

  useEffect(() => {
    let timer;
    if (autoPlay && currentStep < steps.length - 1) {
      timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 5000);
    } else if (autoPlay && currentStep >= steps.length - 1) {
      setAutoPlay(false);
    }
    return () => clearTimeout(timer);
  }, [autoPlay, currentStep, steps.length]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const IconComponent = step?.icon || Info;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="training-tour-dialog">
        {/* Header */}
        <div className="bg-primary text-primary-foreground p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <GraduationCap size={20} />
              <span className="text-sm font-medium">Training Tour</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground">
                {currentStep + 1} / {steps.length}
              </Badge>
              <Button variant="ghost" size="sm" className="text-primary-foreground h-6 w-6 p-0" onClick={handleClose}>
                <X size={16} />
              </Button>
            </div>
          </div>
          <Progress value={progress} className="h-1 bg-primary-foreground/30" />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <IconComponent size={24} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-1">{step?.title}</h3>
              <p className="text-muted-foreground">{step?.description}</p>
            </div>
          </div>

          {/* Tips */}
          {step?.tips && step.tips.length > 0 && (
            <div className="bg-accent/10 border border-accent/20 rounded-md p-3 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb size={16} className="text-accent-foreground" />
                <span className="text-sm font-semibold text-accent-foreground">Tips</span>
              </div>
              <ul className="space-y-1">
                {step.tips.map((tip, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-accent-foreground">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Step indicators */}
          <div className="flex justify-center gap-1 mb-4">
            {steps.map((_, i) => (
              <button
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep ? "bg-primary w-4" : i < currentStep ? "bg-primary/50" : "bg-secondary"
                }`}
                onClick={() => setCurrentStep(i)}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setAutoPlay(!autoPlay)}
              data-testid="tour-autoplay-btn"
            >
              {autoPlay ? <Pause size={16} /> : <Play size={16} />}
              <span className="ml-1">{autoPlay ? "Pause" : "Auto"}</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handlePrev} 
              disabled={currentStep === 0}
              data-testid="tour-prev-btn"
            >
              <ArrowLeft size={16} className="mr-1" />
              Previous
            </Button>
            
            {currentStep < steps.length - 1 ? (
              <Button 
                size="sm" 
                onClick={handleNext}
                data-testid="tour-next-btn"
              >
                Next
                <ArrowRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button 
                size="sm" 
                onClick={handleClose}
                data-testid="tour-finish-btn"
              >
                <CheckCircle size={16} className="mr-1" />
                Finish
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Tour selector component
export function TourSelector({ open, onOpenChange, onSelectTour }) {
  const tours = [
    {
      id: "overview",
      title: "Overview Tour",
      description: "Learn about all 4 views: Org, Function, Process, and System",
      duration: "~3 min",
      icon: GraduationCap,
    },
    {
      id: "orderToDispatch",
      title: "Order to Dispatch",
      description: "Follow the complete journey from PO receipt to product dispatch",
      duration: "~5 min",
      icon: FlowArrow,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap size={24} className="text-primary" />
          <h2 className="text-lg font-bold">Select a Training Tour</h2>
        </div>
        
        <div className="space-y-3">
          {tours.map((tour) => {
            const Icon = tour.icon;
            return (
              <button
                key={tour.id}
                className="w-full p-4 border rounded-md hover:border-primary hover:bg-primary/5 transition-all text-left group"
                onClick={() => onSelectTour(tour.id)}
                data-testid={`tour-option-${tour.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center group-hover:bg-primary/20">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">{tour.title}</h3>
                      <Badge variant="secondary" className="text-xs">{tour.duration}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{tour.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
